import React, { useState } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

const QuickAddModal = ({ type, onSuccess, parentOptions = [], parentType = null, trigger = null }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [loading, setLoading] = useState(false);

  const typeLabels = {
    country: 'Country',
    city: 'City',
    university: 'University',
    campus: 'Campus',
    degree_type: 'Degree Type',
    program: 'Program',
    intake: 'Intake',
    document_type: 'Document Type',
    lead_status: 'Lead Status',
    application_status: 'Application Status',
    offer_status: 'Offer Status'
  };

  const parentLabels = {
    campus: 'University',
    degree_type: 'Campus',
    program: 'Degree Type',
    city: 'Country'
  };

  const needsParent = ['campus', 'degree_type', 'program', 'city'].includes(type);

  const handleQuickAdd = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (needsParent && !parentId) {
      toast.error(`Please select a ${parentLabels[type]?.toLowerCase() || 'parent'}`);
      return;
    }
    setLoading(true);
    try {
      const payload = { 
        type, 
        name: name.trim(), 
        metadata: {},
        parent_id: needsParent ? parentId : null
      };
      await api().post('/master-data', payload);
      toast.success(`${typeLabels[type] || type} added successfully`);
      setOpen(false);
      setName('');
      setParentId('');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  const TriggerComponent = trigger || (
    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/10" title={`Add ${typeLabels[type] || type}`}>
      <Plus className="w-4 h-4" />
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {TriggerComponent}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Quick Add {typeLabels[type] || type}</DialogTitle>
          <DialogDescription>Add a new {(typeLabels[type] || type).toLowerCase()} quickly</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {needsParent && (
            <div className="space-y-2">
              <Label>{parentLabels[type] || 'Parent'}</Label>
              <Select value={parentId} onValueChange={setParentId}>
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${parentLabels[type]?.toLowerCase() || 'parent'}`} />
                </SelectTrigger>
                <SelectContent>
                  {parentOptions.map((opt) => (
                    <SelectItem key={opt.id || opt.value} value={opt.id || opt.value}>
                      {opt.name || opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`Enter ${(typeLabels[type] || type).toLowerCase()} name`}
              onKeyDown={(e) => e.key === 'Enter' && handleQuickAdd()}
              data-testid={`quick-add-${type}-input`}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleQuickAdd} disabled={loading} data-testid={`quick-add-${type}-submit`}>
            {loading ? 'Adding...' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddModal;
